import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-upload-data',
  templateUrl: './upload-data.component.html',
  styleUrls: ['./upload-data.component.scss'],
  standalone: false
})
export class UploadDataComponent implements OnInit {
  token: string | null = null;
  step: 'loading' | 'invalid' | 'request-otp' | 'upload' | 'success' = 'loading';
  
  firstName: string = '';
  maskedPhone: string = '';
  errorMessage: string = '';
  
  otpRequested: boolean = false;
  otpCode: string = '';
  
  selectedFile: File | null = null;
  isDragging: boolean = false;
  isUploading: boolean = false;
  uploadProgress: number = 0;

  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      if (this.token) {
        this.validateToken();
      } else {
        this.step = 'invalid';
        this.errorMessage = 'No secure token provided in the URL.';
      }
    });
  }

  validateToken() {
    this.step = 'loading';
    this.http.get<any>(`${environment.apiUrl}/studentupload/validate-token?token=${this.token}`).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.firstName = res.firstName;
          this.maskedPhone = res.maskedPhoneNumber;
          this.step = 'request-otp';
        } else {
          this.step = 'invalid';
          this.errorMessage = res.message || 'Invalid or expired link.';
        }
      },
      error: (err) => {
        this.step = 'invalid';
        this.errorMessage = err.error?.message || 'Failed to validate link. It may be expired or already used.';
      }
    });
  }

  requestOtp() {
    this.otpRequested = true;
    this.http.post<any>(`${environment.apiUrl}/studentupload/send-otp?token=${this.token}`, {}).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.step = 'upload';
        } else {
          this.otpRequested = false;
          alert(res.message || 'Failed to send OTP.');
        }
      },
      error: (err) => {
        this.otpRequested = false;
        alert(err.error?.message || 'Error occurred while sending OTP.');
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFileChange(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      this.handleFileChange(event.target.files[0]);
    }
  }

  handleFileChange(file: File) {
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Only JPG, PNG and PDF files are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('File size must be less than 5MB.');
      return;
    }
    this.selectedFile = file;
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  uploadFile() {
    if (!this.selectedFile || !this.otpCode || this.otpCode.length < 4) {
      alert('Please provide a file and a valid OTP code.');
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 20;

    const formData = new FormData();
    formData.append('token', this.token!);
    formData.append('otpCode', this.otpCode);
    formData.append('file', this.selectedFile);

    this.http.post<any>(`${environment.apiUrl}/studentupload/upload-data`, formData).subscribe({
      next: (res) => {
        this.uploadProgress = 100;
        this.isUploading = false;
        if (res.isSuccess) {
          this.step = 'success';
        } else {
          alert(res.message || 'Failed to upload document.');
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        alert(err.error?.message || 'Error occurred while uploading the document.');
      }
    });
  }
}
